import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Mail, Users as UsersIcon, Brain, ArrowRight, AlertCircle } from 'lucide-react';
import { images } from '../data/mockData';

const SmallBusiness = () => {
  const challenges = [
    {
      icon: Lock,
      title: 'Password Security',
      problem: 'Staff sharing passwords, using weak ones, or writing them down',
      solution: 'Simple practices that work in your daily reality'
    },
    {
      icon: UsersIcon,
      title: 'Staff Turnover',
      problem: 'Not knowing who still has access to what',
      solution: 'Clear process for handling access when people leave'
    },
    {
      icon: Mail,
      title: 'Email Scams',
      problem: 'Staff clicking on things they shouldn\'t',
      solution: 'Training to spot problems and know what to do'
    },
    {
      icon: Brain,
      title: 'AI Assistance',
      problem: 'Not knowing if AI can help or how to use it safely',
      solution: 'Straight talk about what works and what to avoid'
    }
  ];

  const restaurantChallenges = [
    'POS system security and staff access',
    'Payment card safety',
    'Employee scheduling and communication',
    'Recipe and operational data protection',
    'Online ordering and delivery platform security',
    'Understanding what AI tools can help with inventory or scheduling'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-stone-100 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6 leading-tight">
                For Small Business & Restaurants
              </h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Help with real problems. Explained in terms that make sense for how you actually work.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-[#c84630] text-white font-medium rounded-md hover:bg-[#a83825] transition-colors"
              >
                Talk to us
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
            <div>
              <img
                src={images.business.smallBusiness}
                alt="Small business owners"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Common Challenges */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">
              Problems We Help Small Businesses Solve
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The things that actually keep small business owners up at night
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {challenges.map((item, index) => (
              <div key={index} className="bg-stone-50 p-8 rounded-lg">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#f5e6e0] rounded-lg flex items-center justify-center">
                    <item.icon size={24} className="text-[#c84630]" />
                  </div>
                  <h3 className="ml-4 text-xl font-semibold text-[#1e3a5f]">
                    {item.title}
                  </h3>
                </div>
                <div className="ml-16">
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-500 mb-1">The Problem:</p>
                    <p className="text-gray-700">{item.problem}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">How We Help:</p>
                    <p className="text-gray-700">{item.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Restaurant Specific Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={images.business.restaurant}
                alt="Restaurant operations"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">
                Guidance for Restaurants
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Restaurants have their own problems. High turnover. Rushed training. Payment security. Tight margins. We get it.
              </p>
              <h3 className="font-semibold text-[#1e3a5f] mb-4">
                We help restaurants with:
              </h3>
              <ul className="space-y-3">
                {restaurantChallenges.map((challenge, index) => (
                  <li key={index} className="flex items-start">
                    <AlertCircle size={20} className="text-[#c84630] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AI for Small Business */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">
              AI Tools That Actually Help Small Business
            </h2>
            <p className="text-lg text-gray-600">
              Not hype. Not expensive experiments. Just practical uses that work.
            </p>
          </div>
          <div className="space-y-6">
            <div className="bg-stone-50 p-6 rounded-lg">
              <h3 className="font-semibold text-[#1e3a5f] mb-2">
                Customer Communication
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Draft emails, social media posts, and responses to customer questions faster and more professionally.
              </p>
            </div>
            <div className="bg-stone-50 p-6 rounded-lg">
              <h3 className="font-semibold text-[#1e3a5f] mb-2">
                Document Creation
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Create training materials, policy documents, or job descriptions quickly without starting from scratch.
              </p>
            </div>
            <div className="bg-stone-50 p-6 rounded-lg">
              <h3 className="font-semibold text-[#1e3a5f] mb-2">
                Data Analysis
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Get help understanding your sales patterns, inventory needs, or staffing requirements from your existing data.
              </p>
            </div>
            <div className="bg-stone-50 p-6 rounded-lg">
              <h3 className="font-semibold text-[#1e3a5f] mb-2">
                Translation & Accessibility
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Communicate with customers or staff in multiple languages, or make your materials more accessible.
              </p>
            </div>
          </div>
          <div className="mt-8 p-6 bg-[#f5e6e0] rounded-lg border-l-4 border-[#c84630]">
            <p className="text-gray-800 leading-relaxed">
              <strong className="text-[#1e3a5f]">Important:</strong> We show you how to use these tools without risking your business data or customer information.
            </p>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8">
            Why Small Businesses Work With Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-semibold text-[#1e3a5f] mb-3">
                We Get It
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We understand how small businesses actually work, not just theory.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-[#1e3a5f] mb-3">
                No Pressure
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We don't sell anything except our time. That makes things simple.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-[#1e3a5f] mb-3">
                Local Support
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We're here in West Texas. Real person you can call.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Let's talk about your business needs
          </h2>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">
            Tell us what's not working or what you're worried about. We'll give you straight answers.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#c84630] text-white font-medium rounded-md hover:bg-[#a83825] transition-colors text-lg"
          >
            Get in touch
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SmallBusiness;
